import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Search, Wrench, ShieldOff, Ban, Coins, X } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};
const SORTS = [
  { id: 'fox_dollars', label: 'FOX Dólar' }, { id: 'black_coins', label: 'Black Coins' },
  { id: 'drops', label: 'Gotas' }, { id: 'respect', label: 'Respeito' },
  { id: 'barrels', label: 'Barris' }, { id: 'joined_at', label: 'Entrada' },
];
const CURRENCIES = [
  { id: 'BLACK', label: 'Black Coins' }, { id: 'DROPS', label: 'Gotas' },
  { id: 'FOX', label: 'FOX Dólar' }, { id: 'RESPECT', label: 'Respeito' },
];

const PlayerDetail = ({ seasonId, kakoId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cur, setCur] = useState('BLACK');
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/player/${kakoId}`);
      setD(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao abrir jogador.'); }
  }, [seasonId, kakoId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    if (reason.trim().length < 3) { toast.error('Informe o motivo (mínimo 3 letras).'); return; }
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro na operação.'); }
    setBusy(false);
  };

  if (!d) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;
  const p = d.player;

  return (
    <div className="rounded-xl border-2 border-orange-300 bg-orange-50/40 p-3" data-testid="oil-player-detail">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-black" data-testid="oil-player-name">
            {p.nickname || 'Sem apelido'} <span className="text-xs text-slate-500">· KAKO {p.kako_id}</span>
            {p.suspended && <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-black text-rose-700">SUSPENSO</span>}
          </p>
          <p className="text-[11px] text-slate-500">
            Entrou em {fmtDate(p.joined_at)} · sequência de missões: {d.streak} dia(s) ·
            {' '}golpes dados {d.attacks_made} / sofridos {d.attacks_taken}
          </p>
        </div>
        <button onClick={onClose} data-testid="oil-player-close" className="rounded-full border p-1"><X className="h-4 w-4" /></button>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4">
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Black Coins</p><p className="font-black" data-testid="oil-player-black">{fmt(p.black_coins)}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Gotas</p><p className="font-black">{fmt(p.drops)}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">FOX Dólar</p><p className="font-black">{fmt(p.fox_dollars)}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Respeito</p><p className="font-black">{fmt(p.respect)}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Escudos</p><p className="font-black">{fmt(p.shields)}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Imunidade até</p><p className="text-xs font-bold">{p.immunity_until ? fmtDate(p.immunity_until) : '—'}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Novato até</p><p className="text-xs font-bold">{p.pvp_protection_until ? fmtDate(p.pvp_protection_until) : '—'}</p></div>
        <div className="rounded-lg border bg-white p-2"><p className="text-[10px] uppercase text-slate-400">Barris no galpão</p>
          <p className="text-xs font-bold" data-testid="oil-player-barrels">
            {d.barrels.length ? d.barrels.map((b) => `LV${b.level}×${b.qty}`).join(' · ') : 'nenhum'}
          </p></div>
      </div>

      <div className="mt-2 grid gap-2 md:grid-cols-2">
        {['refinery', 'bottling'].map((kind) => (
          <div key={kind} className="rounded-lg border bg-white p-2" data-testid={`oil-player-facility-${kind}`}>
            <p className="text-[10px] font-bold uppercase text-slate-500">{kind === 'refinery' ? 'Usina' : 'Envasadora'}</p>
            {d.facilities[kind].length === 0 && <p className="text-xs text-slate-400">não construída</p>}
            {d.facilities[kind].map((f) => (
              <p key={f.level} className="flex items-center justify-between text-xs">
                <span>LV.{f.level}</span>
                <b className={f.integrity <= 0 ? 'text-rose-600' : f.integrity < 100 ? 'text-amber-600' : 'text-emerald-600'}>
                  {f.integrity <= 0 ? 'QUEBRADA' : `${f.integrity}%`}
                </b>
              </p>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-lg border bg-white p-2">
        <p className="mb-1 text-[10px] font-black uppercase text-slate-500">Ações (motivo obrigatório, tudo vai para a auditoria)</p>
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="oil-player-reason"
          placeholder="Motivo da ação (ex.: reclamação #1234, correção de bug)"
          className="mb-2 h-9 w-full rounded-lg border px-2 text-xs" />
        <div className="flex flex-wrap items-end gap-2">
          <select value={cur} onChange={(e) => setCur(e.target.value)} data-testid="oil-adjust-currency"
            className="h-9 rounded-lg border bg-white px-2 text-xs">
            {CURRENCIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input type="number" value={amount} onChange={(e) => setAmount(+e.target.value)} data-testid="oil-adjust-amount"
            className="h-9 w-32 rounded-lg border px-2 text-xs" placeholder="+/- valor" />
          <Button size="sm" disabled={busy} data-testid="oil-adjust-submit" className="bg-orange-500 text-white hover:bg-orange-600"
            onClick={() => act(() => api.post(`/admin/oil/season/${seasonId}/player/${kakoId}/adjust`,
              { currency: cur, amount, reason }), 'Saldo ajustado.')}>
            <Coins className="mr-1 h-3.5 w-3.5" /> Ajustar saldo
          </Button>
          <Button size="sm" variant="outline" disabled={busy} data-testid="oil-player-repair"
            onClick={() => act(() => api.post(`/admin/oil/season/${seasonId}/player/${kakoId}/repair`, { reason }),
              'Instalações reparadas.')}>
            <Wrench className="mr-1 h-3.5 w-3.5" /> Zerar dano das instalações
          </Button>
          {['immunity', 'shield', 'rookie'].map((w) => (
            <Button key={w} size="sm" variant="outline" disabled={busy} data-testid={`oil-player-clear-${w}`}
              onClick={() => act(() => api.post(`/admin/oil/season/${seasonId}/player/${kakoId}/clear-protection`,
                { what: w, reason }), 'Proteção limpa.')}>
              <ShieldOff className="mr-1 h-3.5 w-3.5" />
              {w === 'immunity' ? 'Limpar imunidade' : w === 'shield' ? 'Limpar escudo' : 'Limpar novato'}
            </Button>
          ))}
          <Button size="sm" variant={p.suspended ? 'outline' : 'destructive'} disabled={busy} data-testid="oil-player-suspend"
            onClick={() => act(() => api.post(`/admin/oil/season/${seasonId}/player/${kakoId}/suspend`,
              { suspended: !p.suspended, reason }), p.suspended ? 'Jogador reativado.' : 'Jogador suspenso do evento.')}>
            <Ban className="mr-1 h-3.5 w-3.5" /> {p.suspended ? 'Reativar no evento' : 'Suspender do evento'}
          </Button>
        </div>
      </div>

      <p className="mt-3 text-[10px] font-black uppercase text-slate-500">Últimas 50 movimentações</p>
      <div className="max-h-56 overflow-y-auto rounded-lg border bg-white" data-testid="oil-player-ledger">
        {d.ledger.map((l, i) => (
          <p key={i} className="flex items-center justify-between gap-2 border-b px-2 py-1 text-[11px]">
            <span className="text-slate-500">{fmtDate(l.created_at)}</span>
            <span className="flex-1 truncate">{l.reason_label}</span>
            <b className={l.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
              {l.amount >= 0 ? '+' : ''}{fmt(l.amount)} {l.currency}
            </b>
          </p>
        ))}
        {d.ledger.length === 0 && <p className="p-2 text-xs text-slate-400">Nenhuma movimentação.</p>}
      </div>
    </div>
  );
};

export default function OilAdminPlayers({ seasonId }) {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('fox_dollars');
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/oil/season/${seasonId}/players`,
        { params: { q: q || undefined, sort, skip, limit: 25 } });
      setRows(r.data.players || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Erro ao carregar jogadores.'); }
  }, [seasonId, q, sort, skip]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-3" data-testid="oil-admin-players">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} data-testid="oil-players-search"
            placeholder="Buscar por apelido ou KAKO ID"
            className="h-9 w-72 rounded-lg border pl-8 pr-2 text-sm" />
        </div>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setSkip(0); }} data-testid="oil-players-sort"
          className="h-9 rounded-lg border bg-white px-2 text-sm">
          {SORTS.map((s) => <option key={s.id} value={s.id}>Ordenar por {s.label}</option>)}
        </select>
        <span className="text-xs text-slate-500" data-testid="oil-players-total">{fmt(total)} jogadores</span>
      </div>

      {open && <PlayerDetail seasonId={seasonId} kakoId={open} onClose={() => setOpen(null)} onChanged={load} />}

      {!rows ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div> : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr><th className="p-2 text-left">Jogador</th><th className="p-2">FOX</th><th className="p-2">Black</th>
                <th className="p-2">Gotas</th><th className="p-2">Respeito</th><th className="p-2">Barris</th>
                <th className="p-2">Usina/Envas.</th><th className="p-2" /></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.kako_id} className="border-t" data-testid={`oil-player-row-${p.kako_id}`}>
                  <td className="p-2 text-left">
                    <b>{p.nickname || '—'}</b> <span className="text-slate-400">{p.kako_id}</span>
                    {p.suspended && <span className="ml-1 rounded bg-rose-100 px-1 text-[9px] font-black text-rose-700">SUSPENSO</span>}
                  </td>
                  <td className="p-2 text-center">{fmt(p.fox_dollars)}</td>
                  <td className="p-2 text-center">{fmt(p.black_coins)}</td>
                  <td className="p-2 text-center">{fmt(p.drops)}</td>
                  <td className="p-2 text-center">{fmt(p.respect)}</td>
                  <td className="p-2 text-center">{fmt(p.barrels)}</td>
                  <td className="p-2 text-center">{p.refinery_level || 0}/{p.bottling_level || 0}</td>
                  <td className="p-2 text-center">
                    <Button size="sm" variant="outline" data-testid={`oil-player-open-${p.kako_id}`}
                      onClick={() => setOpen(p.kako_id)}>Abrir</Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-3 text-center text-slate-400">Nenhum jogador encontrado.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={skip === 0} data-testid="oil-players-prev"
          onClick={() => setSkip(Math.max(0, skip - 25))}>Anterior</Button>
        <Button size="sm" variant="outline" disabled={skip + 25 >= total} data-testid="oil-players-next"
          onClick={() => setSkip(skip + 25)}>Próximos</Button>
        <span className="text-[11px] text-slate-500">{skip + 1}–{Math.min(skip + 25, total)}</span>
      </div>
    </div>
  );
}
